/**
 * HistoryManager — Generic Undo/Redo stack
 *
 * Usage:
 *   import { HistoryManager } from '@wiz/libs/portal/editor-core/history';
 *
 *   const history = new HistoryManager<MyState>(50);
 *   history.push(snapshot);       // save current state before mutating
 *   const prev = history.undo(currentSnapshot);  // returns previous snapshot
 *   const next = history.redo(currentSnapshot);  // returns next snapshot
 */

export class HistoryManager<T> {
    private _undo: T[] = [];
    private _redo: T[] = [];

    constructor(private maxSize: number = 50) {}

    get canUndo(): boolean { return this._undo.length > 0; }
    get canRedo(): boolean { return this._redo.length > 0; }

    /**
     * Push current snapshot onto undo stack (call BEFORE mutating state).
     * Clears redo stack.
     */
    push(snapshot: T): void {
        this._undo.push(snapshot);
        if (this._undo.length > this.maxSize) this._undo.shift();
        this._redo = [];
    }

    /**
     * Undo: pass the *current* snapshot in, get the previous snapshot back.
     * Returns null if nothing to undo.
     */
    undo(current: T): T | null {
        if (!this.canUndo) return null;
        this._redo.push(current);
        return this._undo.pop()!;
    }

    /**
     * Redo: pass the *current* snapshot in, get the next snapshot back.
     * Returns null if nothing to redo.
     */
    redo(current: T): T | null {
        if (!this.canRedo) return null;
        this._undo.push(current);
        return this._redo.pop()!;
    }

    /** Clear both stacks (e.g. after a fresh load). */
    clear(): void {
        this._undo = [];
        this._redo = [];
    }
}
